import type { ThirdPartyResponsesResponseRow } from '@risksmart-app/drizzle/src/types';
import type { TppGetResponsesSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { TppGetResponsesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createSubscriptionHook } from '../utils/hook-factories';
import { toIso, toIsoOrNull } from './tppResponseMappers';

// Subscription holding pattern (RSP-4320): the tRPC branch polls in place of
// the live socket, at the legacy 30s cadence.
const RESPONSE_LIST_POLL_INTERVAL_MS = 30_000;

type ResponseRow = TppGetResponsesSubscription['third_party_response'][number];

/**
 * Reshapes a tRPC portal list row into the legacy `tppGetResponses`
 * subscription element so the home page and its table config read the same
 * shape on both flag paths. `Status` is the same string-literal union on both
 * sides (domain `ThirdPartyResponseStatus` values equal the GraphQL enum
 * members), so it assigns across without a cast.
 */
const toGraphQLResponse = (
  row: ThirdPartyResponsesResponseRow
): ResponseRow => ({
  __typename: 'third_party_response',
  Id: row.Id,
  Status: row.Status,
  ResponseData: row.ResponseData,
  ParentId: row.ParentId,
  StartDate: toIsoOrNull(row.StartDate),
  ExpiresAt: toIsoOrNull(row.ExpiresAt),
  QuestionnaireTemplateVersionId: row.QuestionnaireTemplateVersionId,
  ModifiedByUser: row.ModifiedByUser,
  ModifiedAtTimestamp: toIso(row.ModifiedAtTimestamp),
  CreatedByUser: row.CreatedByUser,
  CreatedAtTimestamp: toIso(row.CreatedAtTimestamp),
  questionnaireTemplateVersion: row.questionnaireTemplateVersion
    ? {
        __typename: 'questionnaire_template_version',
        Id: row.questionnaireTemplateVersion.Id,
        Version: row.questionnaireTemplateVersion.Version,
        parent: row.questionnaireTemplateVersion.parent
          ? {
              __typename: 'questionnaire_template',
              Id: row.questionnaireTemplateVersion.parent.Id,
              Title: row.questionnaireTemplateVersion.parent.Title,
            }
          : undefined,
      }
    : undefined,
});

/**
 * Flag-gated respondent questionnaire list (RSP-6570). Built on the portal's
 * `createSubscriptionHook`: flag off → the legacy Apollo `tppGetResponses`
 * subscription; flag on → the tRPC `portal.thirdPartyResponses.list` query,
 * polled while the respondent has questionnaires (mirroring the change-request
 * feed poll) so a status flip (Recalled/Rejected) is picked up without a live
 * socket. Both branches return `{ third_party_response }` so the home page
 * never learns which path executed.
 */
const useTppResponsesHook = createSubscriptionHook<
  undefined,
  ThirdPartyResponsesResponseRow[],
  TppGetResponsesSubscription
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.portal.thirdPartyResponses.list.queryOptions(),
  mapTrpcDataToGraphQL: (rows) => ({
    third_party_response: rows.map(toGraphQLResponse),
  }),
  // Poll unconditionally — including when the list is currently EMPTY. Gating
  // the interval on `rows.length > 0` meant a respondent sitting on an empty
  // home page never polled, so a newly-assigned questionnaire never appeared
  // (refetchOnWindowFocus is off, so only a manual reload recovered). The
  // legacy Apollo subscription pushed that first assignment live, and an empty
  // list is exactly when a respondent is waiting for one.
  trpcRefetchInterval: () => RESPONSE_LIST_POLL_INTERVAL_MS,
  graphqlDocument: TppGetResponsesDocument,
});

export const useTppResponses = () =>
  useTppResponsesHook({ queryArgs: undefined });
